import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type Column } from '../components/DataTable';
import type { BoardCommitSummary, PagedResult } from '../lib/types';
import { formatDateTime } from '../lib/format';
import { commitsQueryKey, listCommits } from '../lib/workApi';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const columns: Column<BoardCommitSummary>[] = [
  { header: 'Date', render: (c) => formatDateTime(c.createdAt), cellClass: 'whitespace-nowrap' },
  { header: 'By', render: (c) => c.createdBy ?? '—' },
  { header: 'Note', render: (c) => c.note ?? <span className="text-slate-400">No note</span> },
  {
    header: 'Contents',
    render: (c) => `${plural(c.streamCount, 'stream', 'streams')} · ${plural(c.cardCount, 'card', 'cards')}`,
    cellClass: 'whitespace-nowrap text-slate-600',
  },
  {
    header: '',
    render: (c) => (
      <Link to={`/commits/${c.id}`} id={`commit-view-${c.id}`} className="text-atg-blue font-medium hover:underline">
        View
      </Link>
    ),
    cellClass: 'text-right',
  },
];

/** Newest-first list of board snapshots. The API returns a plain array, shown as one page. */
export function Commits() {
  const commitsQ = useQuery({ queryKey: commitsQueryKey, queryFn: listCommits });
  const items = commitsQ.data;
  const page: PagedResult<BoardCommitSummary> | undefined = items && {
    items,
    totalCount: items.length,
    page: 1,
    pageSize: Math.max(1, items.length),
    totalPages: 1,
  };

  return (
    <div className="w-[95%] mx-auto">
      <h1 className="text-xl font-semibold text-atg-navy mb-4">Commits</h1>
      <DataTable
        data={page}
        isLoading={commitsQ.isLoading}
        error={commitsQ.error}
        options={{}}
        onOptionsChange={() => {}}
        columns={columns}
        rowKey={(c) => c.id}
        emptyState="No commits yet. Use the Commit button on the Work board to take a snapshot."
      />
    </div>
  );
}
